import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { MapPin, BookOpen, Globe, Users, ArrowRight, Compass, Heart, Menu, X, User, Crown, Shield, LogOut, Trophy } from 'lucide-react';
import { MapFilterProvider } from '../contexts/MapFilterContext';
import { InteractiveMap } from './InteractiveMap';
import { FeaturedStories } from './FeaturedStories';
import { Footer } from './Footer';
import { useAuth, LoginForm, UserProfile } from './UserAuth';
import { ClubShowcase } from './ClubShowcase';
import api from '../services/api';

const HERO_IMAGE = 'https://static.prod-images.emergentagent.com/jobs/e126ff91-8a6a-4545-bdbb-44acec104dcb/images/49732186ef18a855ea47d71e8595fa43aa76eda5685b37ef28ddbdcd8387104d.png';

const GuestbookHeader = () => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [showUserProfile, setShowUserProfile] = useState(false);
  const { user, logout } = useAuth();
  const [userPoints, setUserPoints] = useState(0);

  useEffect(() => {
    if (user?.email) {
      api.getUserPoints(user.email).then(d => setUserPoints(d.totalPoints || 0)).catch(() => {});
    }
  }, [user]);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setIsMenuOpen(false);
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 bg-gradient-to-r from-emerald-600 via-teal-500 to-emerald-600 shadow-lg border-b border-emerald-700" style={{ zIndex: 1000 }}>
        <div className="container mx-auto px-2 sm:px-4">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/club/guestbook')} data-testid="guestbook-header-logo">
              <BookOpen className="w-6 h-6 text-emerald-100" />
              <span className="text-lg sm:text-xl font-bold text-white tracking-tight">iWasHere.today</span>
            </div>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-1">
              {[
                { label: 'Map', action: () => scrollToSection('map') },
                { label: 'Stories', action: () => scrollToSection('stories') },
                { label: 'How It Works', action: () => scrollToSection('how-it-works') },
                { label: 'Shop', action: () => navigate('/store?theme=guestbook') },
              ].map(item => (
                <button key={item.label} onClick={item.action} className="text-white/90 hover:text-white transition-colors font-medium px-3 py-1.5 text-sm" data-testid={`guestbook-nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}>
                  {item.label}
                </button>
              ))}

              {user ? (
                <div className="flex items-center gap-2 ml-2">
                  <button onClick={() => setShowUserProfile(true)} className="flex items-center gap-1.5 text-white/90 hover:text-white" data-testid="guestbook-user-profile-btn">
                    <User className="w-4 h-4" />
                    <span className="text-sm">{user.name || user.email.split('@')[0]}</span>
                  </button>
                  {userPoints > 0 && (
                    <span className="bg-emerald-400/30 text-emerald-100 text-xs px-2 py-0.5 rounded-full font-semibold">{userPoints} pts</span>
                  )}
                </div>
              ) : (
                <button onClick={() => setShowLoginForm(true)} className="ml-2 bg-white/20 hover:bg-white/30 text-white px-4 py-1.5 rounded-full text-sm font-medium backdrop-blur-sm border border-white/30" data-testid="guestbook-login-btn">
                  Login
                </button>
              )}
            </nav>

            {/* Mobile Menu */}
            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="md:hidden text-white p-2" data-testid="guestbook-mobile-menu-btn">
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {isMenuOpen && (
            <div className="md:hidden bg-emerald-700/95 backdrop-blur-md border-t border-emerald-600 py-4 px-2 flex flex-col gap-2">
              {['Map', 'Stories', 'How It Works'].map(label => (
                <button key={label} onClick={() => { scrollToSection(label.toLowerCase().replace(/\s/g, '-')); setIsMenuOpen(false); }} className="text-white text-left py-2">{label}</button>
              ))}
              <button onClick={() => { navigate('/store?theme=guestbook'); setIsMenuOpen(false); }} className="text-white text-left py-2">Shop</button>
              {user ? (
                <>
                  <button onClick={() => { setShowUserProfile(true); setIsMenuOpen(false); }} className="text-white text-left py-2 flex items-center gap-2">
                    <User className="w-4 h-4" />{user.name || user.email.split('@')[0]}
                  </button>
                  <button onClick={() => { logout(); setIsMenuOpen(false); }} className="text-red-300 text-left py-2">Sign Out</button>
                </>
              ) : (
                <button onClick={() => { setShowLoginForm(true); setIsMenuOpen(false); }} className="text-white text-left py-2">Login</button>
              )}
            </div>
          )}
        </div>
      </header>

      {showLoginForm && (
        <div style={{ zIndex: 1100, position: 'relative' }}>
          <LoginForm onClose={() => setShowLoginForm(false)} />
        </div>
      )}
      {showUserProfile && (
        <div style={{ zIndex: 1100, position: 'relative' }}>
          <UserProfile user={user} onClose={() => setShowUserProfile(false)} />
        </div>
      )}
    </>
  );
};

const GuestbookHero = () => {
  const navigate = useNavigate();

  return (
    <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-emerald-900 via-teal-800 to-emerald-900">
      <div className="absolute inset-0">
        <img src={HERO_IMAGE} alt="Discover places and sign guestbooks" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/15 to-black/50" />
      </div>

      <div className="relative z-10 container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-400/20 backdrop-blur-md rounded-full border border-emerald-400/40 mb-6">
            <Compass className="w-5 h-5 text-emerald-200" />
            <span className="text-sm text-white font-medium" style={{ textShadow: '0 1px 3px rgba(0,0,0,0.5)' }}>Discover & Explore Guestbook Locations</span>
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-4 leading-tight" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.8), 0 4px 16px rgba(0,0,0,0.4)' }} data-testid="guestbook-hero-title">
            iWasHere.today
          </h1>

          <p className="text-xl sm:text-2xl md:text-3xl font-semibold mb-6" style={{ filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.6))' }}>
            <span className="bg-gradient-to-r from-emerald-300 via-teal-200 to-emerald-300 bg-clip-text text-transparent">
              Every place has a story. Leave yours.
            </span>
          </p>

          <p className="text-base sm:text-lg text-emerald-100 mb-8 max-w-2xl mx-auto leading-relaxed" style={{ textShadow: '0 1px 6px rgba(0,0,0,0.7), 0 2px 10px rgba(0,0,0,0.3)' }}>
            Explore guestbook locations near you, scan the QR code, and add your story.
            See who else has visited and discover hidden gems in your community.
          </p>

          <div className="flex flex-wrap justify-center gap-4 mb-8">
            <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">
              <MapPin className="w-4 h-4 text-emerald-300 inline mr-2" />
              <span className="text-white" style={{ textShadow: '0 1px 3px rgba(0,0,0,0.5)' }}>Guestbook Locations Worldwide</span>
            </div>
            <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">
              <BookOpen className="w-4 h-4 text-emerald-300 inline mr-2" />
              <span className="text-white" style={{ textShadow: '0 1px 3px rgba(0,0,0,0.5)' }}>Sign & Share Your Story</span>
            </div>
            <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">
              <Globe className="w-4 h-4 text-emerald-300 inline mr-2" />
              <span className="text-white" style={{ textShadow: '0 1px 3px rgba(0,0,0,0.5)' }}>Explore Nearby Places</span>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              onClick={() => scrollToMap()}
              className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-bold px-8 py-6 text-lg shadow-2xl shadow-emerald-500/50 hover:shadow-emerald-500/70 transition-all"
              data-testid="guestbook-explore-btn"
            >
              <MapPin className="w-5 h-5 mr-2" />
              Explore Nearby Guestbooks
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate('/create-location')}
              className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg font-bold backdrop-blur-sm"
              data-testid="guestbook-create-btn"
            >
              <BookOpen className="w-5 h-5 mr-2" />
              Create a Guestbook
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

function scrollToMap() {
  document.getElementById('map')?.scrollIntoView({ behavior: 'smooth' });
}

const GuestbookCTA = () => {
  const navigate = useNavigate();

  return (
    <section className="py-16 bg-gradient-to-r from-emerald-600 via-teal-500 to-emerald-600">
      <div className="container mx-auto px-4 text-center">
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
          Start Your Guestbook Today
        </h2>
        <p className="text-lg text-emerald-100 mb-8 max-w-2xl mx-auto">
          Create a guestbook for your business, trail, landmark, or favorite spot.
          Let visitors leave their mark and see who else has been there!
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            size="lg"
            onClick={() => navigate('/store?theme=guestbook')}
            className="bg-white hover:bg-gray-100 text-emerald-700 px-8 py-6 text-lg font-bold"
            data-testid="guestbook-cta-shop-btn"
          >
            Get Guestbook QR Tags
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          <Button
            size="lg"
            variant="outline"
            onClick={() => window.open('https://iwashere.me', '_blank')}
            className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg font-bold"
          >
            Learn More at iWasHere.me
          </Button>
        </div>
      </div>
    </section>
  );
};

export const GuestbookClubPage = () => {
  const guestbookTheme = {
    primary: 'emerald',
    secondary: 'teal',
    accent: 'emerald',
    gradientStyle: 'linear-gradient(to right, #059669, #14b8a6)',
    primaryColor: '#059669',
    secondaryColor: '#14b8a6',
    iconColor: '#047857',
  };

  return (
    <MapFilterProvider initialItemTypeFilter="stationary" theme={guestbookTheme}>
      <div className="min-h-screen bg-emerald-50" data-testid="guestbook-club-page">
        <GuestbookHeader />
        <div className="h-14 sm:h-16" />

        <GuestbookHero />
        <div className="h-16 bg-gradient-to-b from-emerald-900 to-emerald-50" />

        {/* How It Works */}
        <div id="how-it-works" className="bg-emerald-50 py-12">
          <div className="container mx-auto px-4">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-emerald-900 mb-2">How Guestbooks Work</h2>
              <p className="text-emerald-700 max-w-2xl mx-auto">
                Discover locations, sign guestbooks, and share your story in 3 easy steps
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              <div className="bg-white rounded-xl p-6 shadow-md border border-emerald-200 text-center">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Compass className="w-7 h-7 text-emerald-600" />
                </div>
                <h3 className="font-bold text-lg text-emerald-900 mb-2">1. Discover a Location</h3>
                <p className="text-emerald-700 text-sm">
                  Find a guestbook QR code at a business, trail, landmark, or anywhere people gather
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-md border border-emerald-200 text-center">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-emerald-100 flex items-center justify-center">
                  <BookOpen className="w-7 h-7 text-emerald-600" />
                </div>
                <h3 className="font-bold text-lg text-emerald-900 mb-2">2. Sign the Guestbook</h3>
                <p className="text-emerald-700 text-sm">
                  Scan the QR code, add your name, a photo, and a message about your visit
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-md border border-emerald-200 text-center">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Globe className="w-7 h-7 text-emerald-600" />
                </div>
                <h3 className="font-bold text-lg text-emerald-900 mb-2">3. Explore & Connect</h3>
                <p className="text-emerald-700 text-sm">
                  See who else has visited, explore nearby guestbooks, and earn points for your adventures
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Map Section */}
        <div id="map" className="bg-emerald-100/50">
          <div className="container mx-auto px-4 py-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-emerald-900 mb-2">
                Guestbook Locations Near You
              </h2>
              <p className="text-emerald-700">
                Explore guestbook locations and see who's been there
              </p>
            </div>
            <InteractiveMap />
          </div>
        </div>

        {/* Stories Section */}
        <div id="stories" className="bg-emerald-50">
          <div className="container mx-auto px-4 py-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-emerald-900 mb-2">
                Recent Guestbook Entries
              </h2>
              <p className="text-emerald-700">
                Stories and check-ins from explorers around the world
              </p>
            </div>
            <FeaturedStories />
          </div>
        </div>

        <GuestbookCTA />
        <ClubShowcase excludeClubId="guestbook" title="Explore More Communities" />
        <Footer />
      </div>
    </MapFilterProvider>
  );
};

export default GuestbookClubPage;
